import { getItemBarcodeItemInfo } from '../Services/OrderService'
import { barcodeType, weightBarcodeFormat } from '~shared/util/constants'

interface barcodeReaderCallback {
  (barcode: string): void
}

function simulateKeypress(inputString: string): void {
  inputString.split('').forEach(char => {
    const event = new KeyboardEvent('keypress', {
      key: char,
      bubbles: true,
      cancelable: true,
      view: window,
    })
    document.dispatchEvent(event)
  })
}

;(window as any).simulateKeypress = simulateKeypress

export class BarcodeReader {
  private barcode = ''
  private readingBarcode = false
  private callback!: barcodeReaderCallback
  private BarcodeKnownLength: number = 11

  public startMonitoring(callback: barcodeReaderCallback): void {
    this.callback = callback

    // Listen for keypress which links to the barcode scanner
    document.addEventListener('keypress', this.handleScan)
  }

  public stopMonitoring(): void {
    document.removeEventListener('keypress', this.handleScan)
  }

  public getOrderNumberFromBarcode(barcode: string) {
    barcode = this.removeChecksumFromBarcode(barcode)
    barcode = this.removeLeadingZeros(barcode)

    return barcode
  }

  public isWeightedItem(barcode: string): boolean {
    if (barcode) {
      // Trim out any leading 0s
      barcode = this.removeLeadingZeros(barcode)

      // Type 2 UPC
      if (barcode.startsWith('2') && barcode.length > 6) {
        return true
      }
    }

    return false
  }

  public async getItemBarcodeItemInfo(barcode: string): Promise<BarcodeItemInfo | null> {
    //get barcode format of this item
    // search for item by UPC checks item Name, Code, and ExternalId
    var itemBarcodeInfos = await getItemBarcodeItemInfo(barcode)

    if (itemBarcodeInfos && itemBarcodeInfos.length != 1) {
      return null
    }

    return itemBarcodeInfos[0]
  }

  public async calculateWeightFromBarcode(barcodeTotal: number, price: number) {
    let weight = barcodeTotal / price
    return weight.toFixed(3)
  }

  public removeLeadingZeros(barcode: string) {
    return barcode.replace(/^0+/, '')
  }

  //Will return the exact barcode that was entered (including checksum), and validate the checksum if required
  //If validation fails then an empty string is returned.
  private handleScan = (e: KeyboardEvent) => {
    // Scanner throws an 'enter' at the end of read, ignore it.
    if (e.code !== 'Enter' && e.keyCode !== 13) {
      this.barcode += e.key
    }

    // Run a timeout of 150ms at the first read and clear everything
    if (!this.readingBarcode) {
      this.readingBarcode = true

      setTimeout(() => {
        if (this.barcode.length > 10) {
          this.callback(this.barcode)

          this.barcode = ''
        }

        this.barcode = ''
        this.readingBarcode = false
      }, 150)
    }
  }

  public isValidBarcode(value: string) {
    // We only allow correct length barcodes
    if (!value.match(/^(\d{8}|\d{12,14})$/)) {
      return false
    }

    //calculate checksum
    let result = 0
    let counter = 0
    for (let i = value.length - 2; i >= 0; i--) {
      //used to determine odd/even
      counter++

      //starting from the right, even position multiply by 3 (2,4,6,8,etc), odd position multiply by 1 (1,3,5,7,etc))
      result += parseInt(value.charAt(i), 10) * (counter % 2 === 0 ? 1 : 3)
    }

    var checksum = parseInt(value.charAt(value.length - 1), 10)

    return (10 - (result % 10)) % 10 === checksum
  }

  public removeChecksumFromBarcode(barcode: string) {
    //some client's zebra devices do not send the checksum. We don't know which ones,
    //so we only remove the checksum if the barcode is over 11 characters
    if (barcode?.length > this.BarcodeKnownLength) {
      return barcode.slice(0, -1)
    }

    return barcode
  }
}

export type BarcodeItemInfo = {
  price: number | null
  itemCode: string | null
  itemId: string | null
  hasChecksum: boolean
}
